/**
 * A person, in a card, without leaving the page — V6-05 §5.
 *
 * §5 calls this "the existing profile popover". It did not exist: `UserLink`
 * navigated straight to `/people/:id`, which is the right destination and the
 * wrong interaction in a conversation — leaving the thread to find out who
 * somebody is loses your place in it. So this is the popover, built on the
 * data `/users/:id/profile` (V5-08) already returns, and it is shared rather
 * than local to chat because the next screen that names a person will want it.
 *
 * Guests get nothing: the profile endpoint refuses them, and a popover that
 * opens onto an error is worse than a name that does not open.
 */

import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { MessageSquare, UserRound } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { UserAvatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { useAuth } from '@/features/auth';
import { cn } from '@/lib/utils';

interface ProfileData {
  user: {
    _id: string;
    name: string;
    email: string;
    designation?: string;
    systemRole: string;
    avatarUrl?: string;
    status: string;
  };
  teams: { _id: string; name: string }[];
  availability: 'on_leave' | 'half_day' | 'wfh' | null;
}

const AVAILABILITY_LABEL: Record<string, string> = {
  on_leave: 'On leave today',
  half_day: 'Half day',
  wfh: 'Working from home',
};

function useProfile(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['member-profile', userId],
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => (await api.get<ProfileData>(`/users/${userId}/profile`)).data,
  });
}

function Body({ userId, name }: { userId: string; name: string }) {
  const { data, isLoading } = useProfile(userId, true);

  if (isLoading) return <Skeleton className="h-24" />;
  if (!data) {
    return (
      <p className="text-[12px] vt-muted">
        {name} — no further details are available to you.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="profile-popover-body">
      <div className="flex items-center gap-2">
        <UserAvatar
          id={data.user._id}
          name={data.user.name}
          src={data.user.avatarUrl}
          size={36}
        />
        <div className="min-w-0">
          <div className="truncate text-[13px] font-medium">{data.user.name}</div>
          <div className="truncate text-[11px] vt-muted">{data.user.email}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {data.user.designation && (
          <Badge data-testid="popover-designation">{data.user.designation}</Badge>
        )}
        {data.availability && (
          <Badge tone={data.availability === 'wfh' ? 'accent' : 'warn'} data-testid="popover-availability">
            {AVAILABILITY_LABEL[data.availability]}
          </Badge>
        )}
      </div>

      {data.teams.length > 0 && (
        <p className="text-[11px] vt-muted" data-testid="popover-teams">
          {data.teams.map((team) => team.name).join(' · ')}
        </p>
      )}

      <div className="flex gap-1.5">
        <Link to={`/people/${userId}`} data-testid="popover-full-profile">
          <Button variant="secondary" size="sm">
            <UserRound />
            View full profile
          </Button>
        </Link>
        <MessageAction userId={userId} />
      </div>
    </div>
  );
}

/** "Message" is only meaningful for somebody who is not you. */
function MessageAction({ userId }: { userId: string }) {
  const { user } = useAuth();
  if (userId === user?._id) return null;
  return (
    <Link to={`/chat?with=${userId}`} data-testid="popover-message">
      <Button variant="ghost" size="sm">
        <MessageSquare />
        Message
      </Button>
    </Link>
  );
}

export function UserProfilePopover({
  userId,
  name,
  children,
  className,
}: {
  userId: string;
  name: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { user } = useAuth();
  // A guest cannot read a profile, so the trigger is inert rather than a
  // button that opens onto a refusal.
  if (user?.systemRole === 'guest') return <>{children}</>;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="profile-popover-trigger"
          data-user-id={userId}
          title={name}
          className={cn('inline-flex items-center rounded-[4px] hover:text-[var(--accent)]', className)}
          onClick={(event) => event.stopPropagation()}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-3" data-testid="profile-popover">
        <Body userId={userId} name={name} />
      </PopoverContent>
    </Popover>
  );
}
